import { CANCELLATION_POLICIES } from '~/consts/cancellationPolicy';
import { BOARD_TYPES } from '~/consts/ratePlans';

import type {
  ChainDraftRatePlan,
  ChainDraftRatePlanChanges,
  ChainDraftRatePlanUpdate,
} from '~/services/ReservationService';

export const CANCELLATION_POLICY_OPTIONS = [
  'refundable',
  'prior-to-check-in-five-days',
  ...Object.values(CANCELLATION_POLICIES),
  'hidden-cancellation-policy',
];

const financialFields = ['markup', 'commission'] as const;

type FinancialField = (typeof financialFields)[number];

export interface DraftRatePlanEdit {
  plan: ChainDraftRatePlan;
  changes: ChainDraftRatePlanChanges;
  inputValues?: Partial<Record<FinancialField, string>>;
  inputErrors?: Partial<Record<FinancialField, string>>;
}

export interface DraftRatePlanPreview {
  current: Partial<ChainDraftRatePlan> | null;
  proposed: Partial<ChainDraftRatePlan> | null;
  ready: boolean;
  errors: Array<{ code: string; message: string }>;
}

export interface DraftRatePlanBatchResult {
  succeeded: Array<{ id: string; updated_at: string }>;
  failed: Array<{ id: string; error: { code: string; message: string } }>;
}

export interface DraftRatePlanReviewState {
  edits: DraftRatePlanEdits;
  previewRows: Record<string, DraftRatePlanPreview>;
  rowErrors: Record<string, DraftRatePlanRowError>;
  conflicts: Record<string, DraftRatePlanConflict>;
}

export interface DraftRatePlanConflict {
  latest: ChainDraftRatePlan;
}

export interface DraftRatePlanRowError {
  code: string;
  message: string;
  expected_updated_at?: string;
}

export function isDraftRatePlanRefreshRequired(error?: { code: string }): boolean {
  return (
    error?.code === 'stale' ||
    error?.code === 'not_found' ||
    error?.code === 'not_draft' ||
    error?.code === 'outside_chain'
  );
}

export function reconcileDraftRatePlanRefresh(
  state: DraftRatePlanReviewState,
  plans: Array<ChainDraftRatePlan>,
): DraftRatePlanReviewState {
  const conflicts = { ...state.conflicts };
  const rowErrors = { ...state.rowErrors };
  const conflictedIds = new Set<string>();

  plans.forEach((plan) => {
    const edit = state.edits[plan.id];
    const error = rowErrors[plan.id];
    if (
      !edit &&
      error?.code === 'stale' &&
      error.expected_updated_at &&
      plan.updated_at !== error.expected_updated_at
    ) {
      delete rowErrors[plan.id];
    }
    if (edit && plan.updated_at !== edit.plan.updated_at) {
      if (conflicts[plan.id]?.latest.updated_at !== plan.updated_at) {
        conflicts[plan.id] = { latest: plan };
      }
      conflictedIds.add(plan.id);
    }
  });

  return {
    ...state,
    conflicts,
    rowErrors,
    previewRows: Object.fromEntries(
      Object.entries(state.previewRows).filter(([id]) => !conflicts[id] && !conflictedIds.has(id)),
    ),
  };
}

export function discardDraftRatePlanEdit(state: DraftRatePlanReviewState, id: string): DraftRatePlanReviewState {
  const edits = { ...state.edits };
  const rowErrors = { ...state.rowErrors };
  const conflicts = { ...state.conflicts };
  const previewRows = { ...state.previewRows };
  delete edits[id];
  if (!isDraftRatePlanRefreshRequired(rowErrors[id])) {
    delete rowErrors[id];
  }
  delete conflicts[id];
  delete previewRows[id];
  return { ...state, edits, rowErrors, conflicts, previewRows };
}

export function restoreLatestDraftRatePlan(state: DraftRatePlanReviewState, id: string): DraftRatePlanReviewState {
  const next = discardDraftRatePlanEdit(state, id);
  const rowErrors = { ...next.rowErrors };
  delete rowErrors[id];
  return { ...next, rowErrors };
}

export function clearDraftRatePlanRowError(state: DraftRatePlanReviewState, id: string): DraftRatePlanReviewState {
  if (isDraftRatePlanRefreshRequired(state.rowErrors[id])) {
    return state;
  }
  const rowErrors = { ...state.rowErrors };
  delete rowErrors[id];
  return { ...state, rowErrors };
}

export function mergeSavedDraftRatePlan(
  plan: ChainDraftRatePlan,
  proposed: Partial<ChainDraftRatePlan>,
  updatedAt: string,
): ChainDraftRatePlan {
  const normalized = { ...plan };
  if (Object.prototype.hasOwnProperty.call(proposed, 'name') && proposed.name !== undefined) {
    normalized.name = proposed.name;
  }
  if (Object.prototype.hasOwnProperty.call(proposed, 'board_type') && proposed.board_type !== undefined) {
    normalized.board_type = proposed.board_type;
  }
  if (
    Object.prototype.hasOwnProperty.call(proposed, 'cancellation_policy') &&
    proposed.cancellation_policy !== undefined
  ) {
    normalized.cancellation_policy = proposed.cancellation_policy;
  }
  if (Object.prototype.hasOwnProperty.call(proposed, 'rate_type') && proposed.rate_type !== undefined) {
    normalized.rate_type = proposed.rate_type;
  }
  if (Object.prototype.hasOwnProperty.call(proposed, 'markup') && proposed.markup !== undefined) {
    normalized.markup = proposed.markup;
  }
  if (Object.prototype.hasOwnProperty.call(proposed, 'commission') && proposed.commission !== undefined) {
    normalized.commission = proposed.commission;
  }
  normalized.updated_at = updatedAt;
  return normalized;
}

export function updateDraftReviewEdit(
  state: DraftRatePlanReviewState,
  plan: ChainDraftRatePlan,
  field: EditableField,
  value: string | number,
): DraftRatePlanReviewState {
  if (state.conflicts[plan.id] || isDraftRatePlanRefreshRequired(state.rowErrors[plan.id])) {
    return state;
  }
  const rowErrors = { ...state.rowErrors };
  delete rowErrors[plan.id];
  return {
    ...state,
    edits: updateDraftRatePlanEdit(state.edits, plan, field, value),
    previewRows: {},
    rowErrors,
  };
}

export function updateDraftReviewFinancialEdit(
  state: DraftRatePlanReviewState,
  plan: ChainDraftRatePlan,
  field: FinancialField,
  value: string,
): DraftRatePlanReviewState {
  if (state.conflicts[plan.id] || isDraftRatePlanRefreshRequired(state.rowErrors[plan.id])) {
    return state;
  }
  const rowErrors = { ...state.rowErrors };
  delete rowErrors[plan.id];
  return {
    ...state,
    edits: updateDraftFinancialRatePlanEdit(state.edits, plan, field, value),
    previewRows: {},
    rowErrors,
  };
}

export function applyDraftRatePlanBatchResult(
  state: DraftRatePlanReviewState,
  result: DraftRatePlanBatchResult,
  submittedIds: Array<string>,
  expectedUpdatedAtById: Record<string, string> = {},
): DraftRatePlanReviewState {
  const edits = { ...state.edits };
  const rowErrors = { ...state.rowErrors };
  const conflicts = { ...state.conflicts };
  const submitted = new Set(submittedIds);
  const successes = new Map<string, Array<{ id: string; updated_at: string }>>();
  const failures = new Map<string, Array<{ code: string; message: string }>>();
  result.succeeded.forEach((row) => {
    if (submitted.has(row.id)) {
      successes.set(row.id, [...(successes.get(row.id) ?? []), row]);
    }
  });
  result.failed.forEach(({ id, error }) => {
    if (submitted.has(id)) {
      failures.set(id, [...(failures.get(id) ?? []), error]);
    }
  });

  submitted.forEach((id) => {
    const succeeded = successes.get(id) ?? [];
    const failed = failures.get(id) ?? [];
    if (succeeded.length === 1 && failed.length === 0) {
      delete edits[id];
      delete rowErrors[id];
      delete conflicts[id];
    } else if (failed.length === 1 && succeeded.length === 0) {
      rowErrors[id] = {
        ...failed[0],
        ...(failed[0].code === 'stale' && expectedUpdatedAtById[id]
          ? { expected_updated_at: expectedUpdatedAtById[id] }
          : {}),
      };
    } else {
      rowErrors[id] = {
        code: 'missing_result',
        message: 'The server did not return exactly one result for this draft. Refresh before retrying.',
      };
    }
  });

  return {
    ...state,
    edits,
    rowErrors,
    conflicts,
    previewRows: Object.fromEntries(Object.entries(state.previewRows).filter(([id]) => !submitted.has(id))),
  };
}

export function getDraftApprovalError(plan: ChainDraftRatePlan): string | null {
  if (!plan.name?.trim()) {
    return 'Add a name before content approval.';
  }
  if (!BOARD_TYPES.some((board) => board === plan.board_type)) {
    return 'Choose a supported board type before content approval.';
  }
  if (!CANCELLATION_POLICY_OPTIONS.some((policy) => policy === plan.cancellation_policy)) {
    return 'Choose a supported cancellation policy before content approval.';
  }
  if (plan.rate_type !== 'net' && plan.rate_type !== 'gross') {
    return 'Choose a supported rate type before content approval.';
  }
  const field = plan.rate_type === 'net' ? 'markup' : 'commission';
  const percentage = plan[field];
  if (!Number.isFinite(percentage) || percentage <= 0 || percentage > 100) {
    return `Set ${field} above 0 and no greater than 100 before content approval.`;
  }
  return null;
}

export function getApprovableDraftRatePlanIds(
  selectedIds: Array<string>,
  plansById: Record<string, ChainDraftRatePlan>,
  edits: DraftRatePlanEdits,
  conflicts: Record<string, DraftRatePlanConflict> = {},
  rowErrors: Record<string, DraftRatePlanRowError> = {},
): Array<string> {
  return selectedIds.filter((id) => {
    const plan = plansById[id];
    if (!plan || edits[id] || conflicts[id] || rowErrors[id]) {
      return false;
    }
    return getDraftApprovalError(plan) === null;
  });
}

export function getUnavailableDraftRatePlanIds(rowErrors: Record<string, DraftRatePlanRowError>): Array<string> {
  return Object.entries(rowErrors)
    .filter(([, error]) => error.code === 'not_found' || error.code === 'not_draft' || error.code === 'outside_chain')
    .map(([id]) => id);
}

export function reconcileKnownDraftRatePlans(
  knownPlans: Record<string, ChainDraftRatePlan>,
  visiblePlans: Array<ChainDraftRatePlan>,
  selectedIds: Array<string>,
  unavailableIds: Array<string> = [],
): Record<string, ChainDraftRatePlan> {
  const selected = new Set(selectedIds);
  const visible = new Set(visiblePlans.map(({ id }) => id));
  const unavailable = new Set(unavailableIds);
  const next: Record<string, ChainDraftRatePlan> = {};

  Object.entries(knownPlans).forEach(([id, plan]) => {
    if ((selected.has(id) || visible.has(id)) && !unavailable.has(id)) {
      next[id] = plan;
    }
  });
  visiblePlans.forEach((plan) => {
    if (unavailable.has(plan.id)) {
      return;
    }
    const knownPlan = next[plan.id];
    if (!knownPlan || plan.updated_at >= knownPlan.updated_at) {
      next[plan.id] = plan;
    }
  });

  const unchanged =
    Object.keys(knownPlans).length === Object.keys(next).length &&
    Object.entries(knownPlans).every(([id, plan]) => next[id] === plan);
  return unchanged ? knownPlans : next;
}

export function removeUnavailableDraftRatePlanSelections(
  selected: Record<string, boolean>,
  unavailableIds: Array<string>,
): Record<string, boolean> {
  if (unavailableIds.length === 0) {
    return selected;
  }

  const unavailable = new Set(unavailableIds);
  const hasUnavailableSelection = Object.keys(selected).some((id) => selected[id] && unavailable.has(id));
  if (!hasUnavailableSelection) {
    return selected;
  }

  const next = { ...selected };
  unavailable.forEach((id) => delete next[id]);
  return next;
}

function storeDraftRatePlanEdit(
  edits: DraftRatePlanEdits,
  plan: ChainDraftRatePlan,
  changes: ChainDraftRatePlanChanges,
  inputValues: Partial<Record<FinancialField, string>>,
  inputErrors: Partial<Record<FinancialField, string>>,
): DraftRatePlanEdits {
  const next = { ...edits };
  if (
    Object.keys(changes).length === 0 &&
    Object.keys(inputValues).length === 0 &&
    Object.keys(inputErrors).length === 0
  ) {
    delete next[plan.id];
    return next;
  }

  next[plan.id] = {
    plan: edits[plan.id]?.plan ?? plan,
    changes,
    ...(Object.keys(inputValues).length > 0 && { inputValues }),
    ...(Object.keys(inputErrors).length > 0 && { inputErrors }),
  };
  return next;
}

export function updateDraftRatePlanEdit(
  edits: DraftRatePlanEdits,
  plan: ChainDraftRatePlan,
  field: EditableField,
  value: string | number,
): DraftRatePlanEdits {
  const edit = edits[plan.id];
  const changes = { ...(edit?.changes ?? {}), [field]: value };
  const inputValues = { ...(edit?.inputValues ?? {}) };
  const inputErrors = { ...(edit?.inputErrors ?? {}) };
  if (financialFields.includes(field as FinancialField)) {
    delete inputValues[field as FinancialField];
    delete inputErrors[field as FinancialField];
  }

  if (field === 'rate_type' && value !== plan.rate_type) {
    if (value === 'net') {
      changes.commission = 0;
      delete inputValues.commission;
      delete inputErrors.commission;
    } else {
      changes.markup = 0;
      delete inputValues.markup;
      delete inputErrors.markup;
    }
  }

  for (const changedField of editFields) {
    if (Object.is(changes[changedField], plan[changedField])) {
      delete changes[changedField];
    }
  }

  return storeDraftRatePlanEdit(edits, plan, changes, inputValues, inputErrors);
}

export function updateDraftFinancialRatePlanEdit(
  edits: DraftRatePlanEdits,
  plan: ChainDraftRatePlan,
  field: FinancialField,
  inputValue: string,
): DraftRatePlanEdits {
  const edit = edits[plan.id];
  const changes = { ...(edit?.changes ?? {}) };
  const inputValues = { ...(edit?.inputValues ?? {}) };
  const inputErrors = { ...(edit?.inputErrors ?? {}) };
  const value = Number(inputValue);

  if (!inputValue.trim() || !Number.isFinite(value) || value < 0 || value > 100) {
    delete changes[field];
    inputValues[field] = inputValue;
    inputErrors[field] = `Enter a ${field} from 0 to 100.`;
  } else {
    delete inputErrors[field];
    if (Object.is(value, plan[field])) {
      delete changes[field];
      delete inputValues[field];
    } else {
      changes[field] = value;
      inputValues[field] = inputValue;
    }
  }

  return storeDraftRatePlanEdit(edits, plan, changes, inputValues, inputErrors);
}

export function applyBulkDraftRatePlanChanges(
  edits: DraftRatePlanEdits,
  selectedIds: Array<string>,
  plansById: Record<string, ChainDraftRatePlan>,
  changes: BulkDraftRatePlanChanges,
): DraftRatePlanEdits {
  let next = edits;
  const fields = ['board_type', 'cancellation_policy', 'rate_type', 'markup', 'commission'] as const;
  for (const field of fields) {
    const value = changes[field];
    if (value === undefined) {
      continue;
    }
    for (const id of selectedIds) {
      const plan = plansById[id] ?? edits[id]?.plan;
      if (plan) {
        next = updateDraftRatePlanEdit(next, plan, field, value);
      }
    }
  }
  return next;
}

export function buildDraftRatePlanUpdates(
  edits: DraftRatePlanEdits,
  selectedIds: Array<string>,
  conflicts: Record<string, DraftRatePlanConflict> = {},
  rowErrors: Record<string, DraftRatePlanRowError> = {},
): Array<ChainDraftRatePlanUpdate> {
  const selected = new Set(selectedIds);
  return Object.values(edits)
    .filter(
      ({ plan, changes, inputErrors }) =>
        selected.has(plan.id) &&
        !conflicts[plan.id] &&
        !isDraftRatePlanRefreshRequired(rowErrors[plan.id]) &&
        Object.keys(changes).length > 0 &&
        Object.keys(inputErrors ?? {}).length === 0,
    )
    .map(({ plan, changes }) => ({
      id: plan.id,
      expected_updated_at: plan.updated_at,
      changes,
    }));
}

export type DraftRatePlanEdits = Record<string, DraftRatePlanEdit>;
export type BulkDraftRatePlanChanges = Partial<
  Pick<ChainDraftRatePlanChanges, 'board_type' | 'cancellation_policy' | 'rate_type' | 'markup' | 'commission'>
>;

const editFields = ['name', 'board_type', 'cancellation_policy', 'rate_type', 'markup', 'commission'] as const;

type EditableField = (typeof editFields)[number];
